import { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";

/**
 * Значок валюты.
 *
 * Порядок поиска: нарисованный флаг из currencyMeta, затем файл по соглашению
 * `/images/<КОД>.png`, затем кружок с кодом. Благодаря второму шагу флаг для
 * новой валюты добавляется без правки кода — достаточно положить картинку.
 *
 * Эмодзи-флаги здесь не годятся: Windows их не рисует, вместо флага выходят две
 * буквы в рамке.
 */
const CurrencyIcon = ({
  code,
  img,
  size = 26,
  /**
   * Что показать, если картинки нет. В списке уместен кружок с кодом — рядом
   * стоит название. На самой кнопке рядом уже есть код, и кружок повторял бы
   * его дважды, поэтому там значок просто опускается.
   */
  fallback = "badge",
}: {
  code: string;
  img?: string;
  size?: number;
  fallback?: "badge" | "none";
}) => {
  const theme = useTheme();
  const byConvention = `/images/${code.toUpperCase()}.png`;
  const [src, setSrc] = useState<string | null>(img ?? byConvention);

  // Валюта в переключателе меняется без перемонтирования — иначе значок
  // остался бы от прежней.
  useEffect(() => {
    setSrc(img ?? byConvention);
  }, [img, byConvention]);

  if (!src) {
    if (fallback === "none") return null;
    return (
      <Box
        aria-hidden
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          fontSize: size * 0.36,
          fontWeight: 700,
          letterSpacing: 0.2,
          color: theme.palette.accentText,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(99, 91, 255, 0.18)"
              : "rgba(99, 91, 255, 0.12)",
        }}
      >
        {code.slice(0, 3).toUpperCase()}
      </Box>
    );
  }

  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      // Картинки по соглашению может не быть — тогда переходим на кружок.
      onError={() => setSrc(null)}
      style={{ objectFit: "contain", borderRadius: 6, flexShrink: 0 }}
    />
  );
};

export default CurrencyIcon;
